import type { AssetPayload, AssetEntry } from './assetPayload';

/**
 * Compress an alt_title-keyed asset payload into a short single-line text
 * summary, suitable for the `summary` field of the Shopify snapshot-save
 * endpoint (truncated to 1024 chars by the backend — we trim to that
 * length client-side so a runaway summary doesn't waste request bytes).
 *
 * Visible-only: skips entries whose `visibility` is 0 (hidden) or 2 (shop
 * frontend only) — same predicate the savebutton renderer uses for
 * `configuration_table`. The cart-page Edit-link tooltip shows this text
 * to the buyer, so we want only what they'd recognise.
 *
 * Format: `Label: ValueLabel; Label2: ValueLabel2; …` — matches the
 * legacy cloud `Justselling_Configento_App_Summary.getSummaryAsText()`
 * output shape closely enough for downstream tooling (Order summary
 * column on the customer portal etc.) to keep working unchanged.
 */
export function buildSummaryText(payload: AssetPayload): string {
    const parts: string[] = [];

    for (const [, entry] of iterateVisibleEntries(payload)) {
        const valueText = entryValueText(entry);
        if (!valueText) continue;
        parts.push(`${entry.label}: ${valueText}`);
    }

    const out = parts.join('; ');
    return out.length > 1024 ? out.slice(0, 1021) + '…' : out;
}

export interface BuildSummaryOpts {
    /** `text` (default) joins entries with `separator`; `html` emits a
     *  `<dl><dt>Label</dt><dd>Value</dd>…</dl>` block. */
    format?: 'text' | 'html';
    /** Append `(<formattedPrice>)` after the value label when the
     *  Characteristic adds a non-zero amount to the price. Default false. */
    includePrices?: boolean;
    /** Joiner between entries for the text format. Default `'; '`. */
    separator?: string;
}

/**
 * Render the visible-entry subset of an asset payload as a single string
 * for use in saved-configuration mails, cart-line tooltips, and custom storefront
 * summary widgets. text + html only — PDF rendering lives in the
 * server-side Save-Button-Action pipeline; duplicating it client-side
 * would drift.
 *
 * No new i18n keys: the labels and value labels come from the loaded
 * Component (already localised by the bundle's locale picker). Pricing
 * uses Intl.NumberFormat with the host's `WebshopConfig.currency` — caller
 * passes that via opts only when they have a specific override.
 */
export function buildSummary(
    payload: AssetPayload | null,
    opts: BuildSummaryOpts = {},
    /** Lines after the entries, e.g. the combined SKU. */
    trailing: ReadonlyArray<{ label: string; value: string }> = [],
    /** Formatted price per internal name, read when `includePrices` is set. */
    prices: ReadonlyMap<string, string> = new Map(),
): string {
    if (!payload) return '';
    const format        = opts.format ?? 'text';
    const includePrices = opts.includePrices === true;
    const separator     = opts.separator ?? '; ';

    const lines: string[] = [];
    for (const [key, entry] of iterateVisibleEntries(payload)) {
        const valueText = entryValueText(entry);
        if (!valueText) continue;
        const priceText = includePrices ? prices.get(key) ?? '' : '';
        const tail      = priceText ? ` (${priceText})` : '';

        if (format === 'html') {
            lines.push(
                `<dt>${escapeHtml(entry.label)}</dt>` +
                `<dd>${escapeHtml(valueText)}${escapeHtml(tail)}</dd>`,
            );
        } else {
            lines.push(`${entry.label}: ${valueText}${tail}`);
        }
    }

    if (lines.length > 0) {
        for (const { label, value } of trailing) {
            lines.push(format === 'html'
                ? `<dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd>`
                : `${label}: ${value}`);
        }
    }

    if (format === 'html') {
        return lines.length > 0 ? `<dl>${lines.join('')}</dl>` : '';
    }
    return lines.join(separator);
}

function* iterateVisibleEntries(payload: AssetPayload): Generator<[string, AssetEntry]> {
    for (const [key, entry] of Object.entries(payload)) {
        if (key.startsWith('_')) continue;  // _component_title / _locale / _combined_image
        if (key === 'altcha') continue;
        if (!isAssetEntry(entry)) continue;
        if (entry.visibility !== 1 && entry.visibility !== 3) continue;
        yield [key, entry];
    }
}

function isAssetEntry(v: unknown): v is AssetEntry {
    return typeof v === 'object' && v !== null && 'label' in v && 'visibility' in v;
}

/**
 * The entry's value as text, with "2x " in front of each label that carries
 * a quantity — legacy's summary does the same (app/summary.js).
 */
function entryValueText(entry: AssetEntry): string {
    const labels = entry.valueLabel ?? entry.value;
    const qty = entry.valueQty;
    if (qty === undefined) {
        return stringifyValueLabel(entry.valueLabel) || stringifyValueLabel(entry.value);
    }
    if (Array.isArray(labels) && Array.isArray(qty)) {
        return stringifyValueLabel(labels.map((l, i) => (l === '' || l == null ? l : `${qty[i] ?? 1}x ${l}`)));
    }
    const single = stringifyValueLabel(labels);
    return single && typeof qty === 'number' ? `${qty}x ${single}` : single;
}

function stringifyValueLabel(v: unknown): string {
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'number') return String(v);
    if (Array.isArray(v)) {
        return v
            .filter((x) => x !== '' && x !== null && x !== undefined)
            .join(', ')
            .trim();
    }
    return '';
}

function escapeHtml(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
